import { useMemo } from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { defaultChartOptions } from '@/components/dashboard/charts/chartConfig';
import {
  GroupTimeSeriesPoint,
  MetricKey,
  DEFAULT_METRIC_TOGGLES,
} from '@/types/reports';
import type { DeploymentPeriod } from '@/types/history';
import { generateDeploymentAnnotations } from '@/lib/chartAnnotations';

interface GroupLineChartProps {
  data: GroupTimeSeriesPoint[];
  enabledMetrics: MetricKey[];
  title?: string;
  /** Dynamic label for group (Production vs Group based on industry) */
  groupLabel?: string;
  /** Deployment periods for chart annotations (internal users only) */
  deployments?: DeploymentPeriod[];
  /** Whether to show deployment annotations on the chart */
  showDeploymentAnnotations?: boolean;
  /** Chart date range for annotation boundaries */
  dateRange?: { start: Date | null; end: Date | null };
}

export function GroupLineChart({
  data,
  enabledMetrics,
  title = 'Production Metrics Over Time',
  groupLabel = 'production',
  deployments = [],
  showDeploymentAnnotations = false,
  dateRange,
}: GroupLineChartProps) {
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS (Rules of Hooks)

  // Get enabled metric configurations
  const activeMetrics = useMemo(
    () => DEFAULT_METRIC_TOGGLES.filter(m => enabledMetrics.includes(m.key)),
    [enabledMetrics]
  );

  // Separate metrics by Y-axis group
  const { absoluteMetrics, percentMetrics } = useMemo(
    () => ({
      absoluteMetrics: activeMetrics.filter(m => m.yAxisGroup === 'absolute'),
      percentMetrics: activeMetrics.filter(m => m.yAxisGroup === 'percent'),
    }),
    [activeMetrics]
  );

  // Build Y-axis configuration - TWO axes (dual-axis), not one per series
  // Group all absolute metrics (kWh, kg, $) on left axis, all percent metrics on right axis
  const yaxisConfig = useMemo<ApexYAxis[]>(() => {
    const config: ApexYAxis[] = [];

    // Left axis for absolute values (kWh, kg, $)
    if (absoluteMetrics.length > 0) {
      const absoluteSeriesNames = absoluteMetrics.map(m => m.label);
      config.push({
        // seriesName can be a string or array - use array to group multiple series on one axis
        seriesName: absoluteSeriesNames.length === 1 ? absoluteSeriesNames[0] : absoluteSeriesNames,
        show: true,
        opposite: false,
        title: {
          text: 'kWh / kg / $',
          style: { color: '#9ca3af', fontSize: '11px', fontWeight: 500 },
        },
        labels: {
          formatter: (val: number) => {
            if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
            return Math.round(val).toString();
          },
          style: { colors: '#9ca3af', fontSize: '10px' },
        },
        min: 0,
      });
    }

    // Right axis for percentages
    if (percentMetrics.length > 0) {
      const percentSeriesNames = percentMetrics.map(m => m.label);
      config.push({
        seriesName: percentSeriesNames.length === 1 ? percentSeriesNames[0] : percentSeriesNames,
        show: true,
        opposite: true,
        title: {
          text: '%',
          style: { color: '#9ca3af', fontSize: '11px', fontWeight: 500 },
        },
        labels: {
          formatter: (val: number) => `${Math.round(val)}%`,
          style: { colors: '#9ca3af', fontSize: '10px' },
        },
        min: 0,
        max: 100,
      });
    }

    // Edge case: No metrics selected - provide fallback single axis
    if (config.length === 0) {
      config.push({
        show: true,
        labels: {
          style: { colors: '#9ca3af', fontSize: '10px' },
        },
      });
    }

    return config;
  }, [absoluteMetrics, percentMetrics]);

  // Determine if we should use datetime axis (for annotations support)
  const useDatetimeAxis = showDeploymentAnnotations && deployments.length > 0 && dateRange?.start && dateRange?.end;

  // Build series data - separate implementations for type safety
  // Categorical axis uses number[] for data, datetime uses {x, y}[] format
  const categoricalSeries = useMemo(
    () =>
      activeMetrics.map(metric => ({
        name: metric.label,
        data: data.map(point => {
          const value = point[metric.key as keyof GroupTimeSeriesPoint];
          return typeof value === 'number' ? value : 0;
        }),
      })),
    [data, activeMetrics]
  );

  const datetimeSeries = useMemo(
    () =>
      activeMetrics.map(metric => ({
        name: metric.label,
        data: data.map(point => {
          const value = point[metric.key as keyof GroupTimeSeriesPoint];
          return {
            x: new Date(point.date).getTime(),
            y: typeof value === 'number' ? value : 0,
          };
        }),
      })),
    [data, activeMetrics]
  );

  // Select appropriate series based on axis type
  const series = useDatetimeAxis ? datetimeSeries : categoricalSeries;

  // Generate deployment annotations
  const deploymentAnnotations = useMemo(() => {
    if (!showDeploymentAnnotations || deployments.length === 0 || !dateRange?.start || !dateRange?.end) {
      return [];
    }
    return generateDeploymentAnnotations(deployments, dateRange.start, dateRange.end);
  }, [showDeploymentAnnotations, deployments, dateRange]);

  // Build chart options with useMemo for stable reference
  const options = useMemo<ApexOptions>(
    () => ({
      ...defaultChartOptions,
      chart: {
        ...defaultChartOptions.chart,
        type: 'area',
        height: 280,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: activeMetrics.map(m => m.color),
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      markers: {
        size: 0,
        hover: { size: 5 },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.3,
          opacityTo: 0.05,
          stops: [0, 100],
        },
      },
      xaxis: useDatetimeAxis
        ? {
            type: 'datetime',
            labels: {
              style: { colors: '#9ca3af', fontSize: '10px' },
              rotate: 0,
              rotateAlways: false,
              hideOverlappingLabels: true,
              datetimeUTC: false,
              format: 'MMM dd',
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
          }
        : {
            categories: data.map(d => d.periodLabel),
            labels: {
              style: { colors: '#9ca3af', fontSize: '10px' },
              rotate: 0,
              rotateAlways: false,
              hideOverlappingLabels: true,
              trim: false,
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
          },
      // Add deployment annotations when enabled
      // Always provide full annotations structure to prevent ApexCharts internal crash
      // when accessing annotations.images on undefined
      annotations: {
        xaxis: deploymentAnnotations.length > 0 ? deploymentAnnotations : [],
        yaxis: [],
        points: [],
        texts: [],
        images: [],
      },
      yaxis: yaxisConfig,
      grid: {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        strokeDashArray: 3,
      },
      legend: {
        show: true,
        showForSingleSeries: true,
        position: 'bottom',
        horizontalAlign: 'center',
        labels: { colors: '#9ca3af' },
        markers: {
          size: 8,
          offsetX: -4,
          offsetY: 0,
        },
        itemMargin: { horizontal: 16, vertical: 8 },
        fontSize: '11px',
        floating: false,
        // Force horizontal layout with no grouping by using custom container style
        containerMargin: { left: 0, top: 0 },
        onItemClick: { toggleDataSeries: true },
        onItemHover: { highlightDataSeries: true },
      },
      tooltip: {
        theme: 'dark',
        shared: true,
        intersect: false,
        y: {
          formatter: (val: number, opts) => {
            const seriesIndex = opts.seriesIndex;
            const metric = activeMetrics[seriesIndex];
            if (!metric) return val.toString();

            if (metric.unit === '%') return `${Math.round(val)}%`;
            if (metric.unit === '$') return `$${val.toLocaleString()}`;
            if (metric.unit === 'kg') return `${val.toLocaleString()} kg`;
            return `${val.toLocaleString()} ${metric.unit}`;
          },
        },
      },
    }),
    [data, activeMetrics, yaxisConfig, useDatetimeAxis, deploymentAnnotations]
  );

  // Empty state - rendered AFTER all hooks are called
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-[#8FB560]" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[280px] flex items-center justify-center">
          <div className="text-center">
            <TrendingUp className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">Select a {groupLabel.toLowerCase()} to view metrics</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#8FB560]" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 md:px-6 md:pb-2">
        <Chart
          key={enabledMetrics.join('-')}
          type="area"
          height={280}
          options={options}
          series={series}
        />
      </CardContent>
    </Card>
  );
}

// Also export as GroupBarChart for backwards compatibility
export { GroupLineChart as GroupBarChart };
